import { safeRetrieve, safeStore } from '../../utils/storage';
import { StreamrAppService } from '../streamrapp/StreamrAppService';
import { DestinationsServiceOutput, } from './DestinationsServiceOutput';
export class DestinationsService {
    static instance;
    output;
    /**
     * Maps RTMP destinationId (from RtmpPublishManager) back to PlatformId
     * so status callbacks can update the correct platform.
     */
    destinationIdToPlatform = new Map();
    async init() {
        this.loadFromStorage();
    }
    static getInstance() {
        if (!DestinationsService.instance) {
            DestinationsService.instance = new DestinationsService();
        }
        return DestinationsService.instance;
    }
    static getOutput() {
        return DestinationsServiceOutput.getReactiveOutput();
    }
    constructor() {
        this.output = new DestinationsServiceOutput();
    }
    /**
     * Load saved destination configs from localStorage.
     * All start as disabled/disconnected.
     */
    loadFromStorage() {
        const saved = safeRetrieve('rtmpDestinations', (v) => v);
        if (!saved)
            return;
        for (const [platform, config] of Object.entries(saved)) {
            if (!config)
                continue;
            const pid = platform;
            this.output.setDestination(pid, {
                config: {
                    platform: pid,
                    serverUrl: config.serverUrl,
                    streamKey: config.streamKey,
                },
                enabled: false,
                status: 'disconnected',
            });
        }
    }
    saveToStorage() {
        const all = this.output.getAllDestinations();
        const toSave = {
            youtube: undefined,
            twitch: undefined,
            x: undefined,
            linkedin: undefined,
        };
        for (const [platform, state] of Object.entries(all)) {
            if (state?.config) {
                toSave[platform] = {
                    serverUrl: state.config.serverUrl,
                    streamKey: state.config.streamKey,
                };
            }
        }
        safeStore('rtmpDestinations', toSave);
    }
    configureDestination(platform, serverUrl, streamKey) {
        const existing = this.output.getDestination(platform);
        const state = {
            config: { platform, serverUrl, streamKey },
            enabled: existing?.enabled ?? false,
            status: existing?.status ?? 'disconnected',
            error: existing?.error,
            destinationId: existing?.destinationId,
        };
        this.output.setDestination(platform, state);
        this.saveToStorage();
    }
    async removeDestination(platform) {
        const existing = this.output.getDestination(platform);
        if (existing?.enabled) {
            await this.disableDestination(platform);
        }
        this.output.setDestination(platform, undefined);
        this.saveToStorage();
    }
    async enableDestination(platform) {
        const state = this.output.getDestination(platform);
        if (!state?.config) {
            console.warn(`[DestinationsService] Cannot enable ${platform}: not configured`);
            return;
        }
        const rtmpUrl = `${state.config.serverUrl}/${state.config.streamKey}`;
        this.output.updateDestination(platform, {
            enabled: true,
            status: 'connecting',
            error: undefined,
        });
        try {
            const service = StreamrAppService.getInstance();
            const destinationId = await service.notifyAddRtmpDestination(rtmpUrl, undefined, (id, status, error) => {
                this.handleStatusChange(id, status, error);
            });
            if (destinationId) {
                this.destinationIdToPlatform.set(destinationId, platform);
                this.output.updateDestination(platform, {
                    destinationId,
                    status: 'publishing',
                });
            }
        }
        catch (err) {
            const errorMsg = err instanceof Error ? err.message : String(err);
            this.output.updateDestination(platform, {
                enabled: false,
                status: 'error',
                error: errorMsg,
            });
        }
    }
    async disableDestination(platform) {
        const state = this.output.getDestination(platform);
        if (state?.destinationId) {
            const service = StreamrAppService.getInstance();
            await service.notifyRemoveRtmpDestination(state.destinationId);
            this.destinationIdToPlatform.delete(state.destinationId);
        }
        this.output.updateDestination(platform, {
            enabled: false,
            status: 'disconnected',
            error: undefined,
            destinationId: undefined,
        });
    }
    /**
     * Reset all destination states to disabled/disconnected without issuing
     * RTMP removal calls. Used when the broadcast has already been torn down
     * (e.g. "End Broadcast") and the underlying sessions no longer exist.
     */
    resetAllDestinations() {
        const all = this.output.getAllDestinations();
        for (const [platform, state] of Object.entries(all)) {
            if (!state)
                continue;
            if (state.destinationId) {
                this.destinationIdToPlatform.delete(state.destinationId);
            }
            this.output.updateDestination(platform, {
                enabled: false,
                status: 'disconnected',
                error: undefined,
                destinationId: undefined,
            });
        }
    }
    handleStatusChange(destinationId, status, error) {
        const platform = this.destinationIdToPlatform.get(destinationId);
        if (!platform) {
            console.warn(`[DestinationsService] Unknown destinationId: ${destinationId}`);
            return;
        }
        const mappedStatus = status;
        this.output.updateDestination(platform, {
            status: mappedStatus,
            error,
        });
    }
}
